/*
 * Drag-and-drop moves between published folders.
 *
 * In the Library a drop moves the asset record itself. In a publishing
 * endpoint an asset is published *into* folders, so a "move" from one
 * published folder to another is really: publish into the destination, then
 * unpublish from the source. Both the sidebar tree and the grid folder cards
 * gate their drop targets through canMovePublishedAssets, and both hand the
 * drop to endpointActions.movePublishedAssets.
 */
import { isConsumedId } from './sharedEndpoints'
import {
  APPROVED,
  PENDING_APPROVAL,
  PENDING_REMOVAL,
} from '../actions/endpoint'
import { buildPath, getEndpointOfNode, validatePrivilege } from './utils'

/*
 * The set of assets a drag carries. Drags that start in a publishing endpoint
 * do not add the dragged card to the selection (selection there is
 * status-gated), so fall back to just the dragged asset when it is not among
 * the selected ones.
 */
export function getDraggedAssets(draggedId, selectedAssets) {
  const selected = selectedAssets || []
  return selected.indexOf(draggedId) > -1 ? selected : [draggedId]
}

/*
 * True when the user has the privileges needed to move published assets from
 * sourceFolder to destFolder, and the assets are in a state that permits it:
 *   - both folders belong to the same publishing endpoint (never the Library)
 *   - neither folder is consumed shared-endpoint content or in the recycle bin
 *   - every asset is APPROVED in the source
 *   - no asset is already live or awaiting review in the destination
 *   - user may unpublish from the source and publish to the destination
 * Whether the move takes effect immediately or becomes a review request is
 * decided later (see movePublishedAssetsSaga); this only says a drop is
 * allowed at all.
 */
export function canMovePublishedAssets({
  assets,
  sourceFolder,
  destFolder,
  allFolders,
  endpoints,
  folderAssetStatus,
  privileges,
  email,
  groups,
  recycleBin,
}) {
  if (!assets || assets.length === 0) return false
  if (!sourceFolder || !destFolder || sourceFolder === destFolder) return false
  const source = allFolders[sourceFolder]
  const dest = allFolders[destFolder]
  if (!source || !dest || source.deleted || dest.deleted) return false
  if (dest.virtual || isConsumedId(sourceFolder) || isConsumedId(destFolder))
    return false
  if (recycleBin && buildPath(destFolder, allFolders).indexOf(recycleBin) > -1)
    return false

  const endpointList = (endpoints && endpoints.endpoints) || []
  const sourceEndpoint = getEndpointOfNode(source, allFolders, endpointList)
  const destEndpoint = getEndpointOfNode(dest, allFolders, endpointList)
  if (
    !sourceEndpoint.id ||
    sourceEndpoint.id === 'LIB' ||
    !sourceEndpoint.allowPublishing ||
    sourceEndpoint.id !== destEndpoint.id
  )
    return false

  const statusIn = (folderId, assetId) => {
    const folderStatus = folderAssetStatus && folderAssetStatus[folderId]
    const record = folderStatus && folderStatus[assetId]
    return record ? record.status : undefined
  }
  const movable = assets.every((assetId) => {
    if (statusIn(sourceFolder, assetId) !== APPROVED) return false
    const destStatus = statusIn(destFolder, assetId)
    return (
      destStatus !== APPROVED &&
      destStatus !== PENDING_APPROVAL &&
      destStatus !== PENDING_REMOVAL
    )
  })
  if (!movable) return false

  return (
    validatePrivilege(
      'unpublishasset',
      privileges,
      email,
      groups,
      sourceFolder,
      allFolders,
    ) &&
    validatePrivilege(
      'publishasset',
      privileges,
      email,
      groups,
      destFolder,
      allFolders,
    )
  )
}
